import api from './api';

/**
 * navigationRegistry — one fetch of the caller's page gating, shared by the
 * sidebar (Phase 2) and the router (Phase 3).
 *
 * Both ask the same question of the same endpoint. Before this module the
 * sidebar fetched it on mount; adding a second consumer that fetched per route
 * change would have meant a request on every navigation. The payload is
 * therefore loaded ONCE per session and held here, and the server caches it for
 * 120s per user on top of that.
 *
 * **The last rung is non-negotiable.** Any failure — unmigrated tenant,
 * unseeded table, network error, timeout — resolves to `EMPTY`, which means
 * `configured: false`: the sidebar falls back to its compiled permission checks
 * and the router does not gate at all. Making gating configurable must not give
 * the application a new way to fail.
 */

/** Timeout after which we stop waiting and behave as if unconfigured. Without
 *  it a hanging request would leave every page on a spinner — a gating feature
 *  must not be able to take the application down. */
const LOAD_TIMEOUT_MS = 6000;

export const EMPTY = {
  configured: false,
  decisions: {},
  routes: {},
  reasons: {},
  gaps: [],
  routeGates: {},
  // A tenant's display names for menu entries, by key; absent = compiled label.
  labels: {},
  // Absent settings must read as the product defaults, never as "deny".
  enforcement: { enforce_routes: true, deny_unknown_routes: false },
};

let inflight = null;
let cached = null;

const normalise = (data) => {
  if (!data || !data.configured) return EMPTY;
  return {
    configured: true,
    isAdmin: Boolean(data.is_admin),
    decisions: data.decisions || {},
    routes: data.routes || {},
    reasons: data.reasons || {},
    gaps: data.gaps || [],
    routeGates: data.route_gates || {},
    labels: data.labels || {},
    enforcement: {
      ...EMPTY.enforcement,
      ...(data.enforcement || {}),
    },
  };
};

/** The caller's gating payload. Resolves to `EMPTY` on any failure. */
export const loadNavigationRegistry = () => {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  const request = api
    .get('/users/api/navigation/', { timeout: LOAD_TIMEOUT_MS })
    .then(({ data }) => normalise(data))
    .catch(() => EMPTY)
    .then((result) => {
      cached = result;
      inflight = null;
      return result;
    });

  inflight = request;
  return request;
};

/** Drop the cached payload — call on logout, or after an administrator edits
 *  gating, so the next read reflects it without a full page reload. */
export const resetNavigationRegistry = () => {
  cached = null;
  inflight = null;
};

/** The payload if it is already loaded, else null. Lets a consumer render
 *  synchronously on every navigation after the first. */
export const peekNavigationRegistry = () => cached;

// ─── Route matching ─────────────────────────────────────────────────────────

/**
 * MIRROR OF `apps/users/api/route_matching.py` — keep the two in step, or the
 * router and the coverage report will disagree about the same URL.
 *
 * 1. Query string, hash and any trailing slash are ignored.
 * 2. An exact match wins outright.
 * 3. Otherwise the longest registry route that is a SEGMENT-BOUNDARY prefix
 *    governs, so `/crm/sales-orders/4821` inherits `/crm/sales-orders`.
 *    The boundary matters: `/admin` must not swallow `/administration/x`.
 * 4. The root route `/` is never a prefix — it would govern the whole app.
 * 5. No match → unknown, and unknown is ALLOWED unless the tenant opted into
 *    `deny_unknown_routes`.
 */
export const normalisePath = (path) => {
  if (!path) return '/';
  let out = String(path).split('?')[0].split('#')[0];
  if (!out.startsWith('/')) out = `/${out}`;
  while (out.length > 1 && out.endsWith('/')) out = out.slice(0, -1);
  return out;
};

/** The registry route governing `path`, or null. `routes` is an object keyed by
 *  route (the `routes` map from the payload) or an array of routes. */
export const matchRoute = (path, routes) => {
  const target = normalisePath(path);
  const keys = Array.isArray(routes) ? routes : Object.keys(routes || {});

  let exact = null;
  let best = null;      // the matched route, as the caller supplied it
  let bestLength = -1;  // length of its normalised form — longest prefix wins
  keys.forEach((raw) => {
    if (!raw) return;
    const norm = normalisePath(raw);
    if (norm === target) {
      exact = raw;
      return;
    }
    if (norm === '/') return; // rule 4
    if (target.startsWith(`${norm}/`) && norm.length > bestLength) {
      best = raw;
      bestLength = norm.length;
    }
  });
  return exact || best;
};
